import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardCheck,
  Compass,
  FolderKanban,
  Layers,
  Map,
  Scale,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import { useAuth } from "@/context/AuthContext";
import LegalDisclaimer from "@/components/agreements/LegalDisclaimer";
import CategoryIcon from "@/components/agreements/CategoryIcon";
import DocumentCard from "@/components/agreements/DocumentCard";
import { CATEGORIES, ACCENT_CLASSES } from "@/data/agreements/categories";
import { DOCUMENTS, DOC_BY_ID, FEATURED_DOC_IDS } from "@/data/agreements/documentLibrary";
import { TEMPLATES } from "@/data/agreements/templateLibrary";
import { BUNDLES } from "@/data/agreements/bundles";
import { ROADMAP } from "@/data/agreements/stages";
import { CHECKLIST_PHASES } from "@/data/agreements/masterChecklist";
import { useAgreementProject } from "@/hooks/useAgreementProject";

/**
 * AgreementsDashboard — route: /agreements
 *
 * Hero + disclaimer + the "where am I" plan card + the tile grid (finder, 20 categories,
 * bundles, roadmap, checklist) + the documents most founders should read first.
 *
 * Every number on this page is derived from the data files. Nothing is hard-coded.
 */
export default function AgreementsDashboard() {
  const { completeness, plan, progress } = useAgreementProject();
  const { user } = useAuth() || {};
  const isAdmin = !!(user && (user.is_admin || user.role === "admin"));

  const countByCategory = useMemo(() => {
    const map = {};
    for (const doc of DOCUMENTS) map[doc.category] = (map[doc.category] || 0) + 1;
    return map;
  }, []);

  const featured = useMemo(
    () => FEATURED_DOC_IDS.map((id) => DOC_BY_ID[id]).filter(Boolean),
    [],
  );

  return (
    <AgreementsShell wide hero={false}>
      {/* ── Hero (Welcome-page style: gradient stripe + rose eyebrow + gradient underline) ── */}
      <section className="relative mb-8 overflow-hidden rounded-2xl border border-neutral-800/40 bg-neutral-900 text-white shadow-lg">
        <div className="h-1 w-full bg-gradient-to-r from-rose-500 via-pink-400 via-fuchsia-500 to-sky-500" />
        {/* Right-side hero image (CPG Agreements photograph). Served from
            the Emergent public asset CDN so it works identically in preview
            and production. Hidden on mobile to keep the copy readable; on
            md+ screens it fills the right ~50%, and a left-to-right dark
            gradient guarantees the heading always has contrast. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 bg-cover bg-center bg-no-repeat md:block lg:w-[55%]"
          style={{
            backgroundImage:
              "url('https://customer-assets-wrfwihn1.emergentagent.net/job_cpg-institute-ux/artifacts/4arcadi8_CPG%20Agreements.png')",
          }}
          data-testid="agreements-hero-bg-image"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-neutral-900 via-neutral-900/85 to-transparent md:block"
        />
        <div className="relative p-6 sm:p-10">
          <div className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-400">
              <Scale className="h-3.5 w-3.5" /> CPG Agreements
            </p>
            {isAdmin && (
              <Link
                to="/admin/agreements-authoring"
                className="mb-3 ml-2 inline-flex items-center gap-2 rounded-full bg-violet-500/20 border border-violet-300/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-violet-100 hover:bg-violet-500/30"
                data-testid="agreements-admin-chip"
              >
                Author templates
              </Link>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Every agreement a CPG brand signs — explained before you sign it.
            </h1>
            <span className="mt-3 block h-0.5 w-16 rounded-full bg-gradient-to-r from-rose-500 via-fuchsia-500 to-sky-500" aria-hidden="true" />
            <p className="mt-4 text-sm text-neutral-300 sm:text-base">
              What the document does, what it costs you when it is written badly, and the exact clauses
              to argue about — written for food, beverage and supplement founders, not for lawyers.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/95 font-semibold">
                <Link to="/agreements/intake" data-testid="agreements-cta-intake">
                  <FolderKanban className="mr-2 h-4 w-4" /> Start the brand intake
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/agreements/library" data-testid="agreements-cta-library">
                  <Search className="mr-2 h-4 w-4" /> Browse the library
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-xs uppercase tracking-widest text-neutral-500">
              {DOCUMENTS.length} documents · {CATEGORIES.length} categories · {TEMPLATES.length} starting templates
            </p>
          </div>
        </div>
      </section>

      <LegalDisclaimer variant="library" className="mb-6" />

      {/* ── Plan state card ──────────────────────────────────────────────── */}
      <PlanCard completeness={completeness} plan={plan} progress={progress} />

      {/* ── Tiles ────────────────────────────────────────────────────────── */}
      <section className="mb-10">
        <div className="mb-4">
          <p className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
            Get started
          </p>
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900">
            Where do you want to <span className="bg-gradient-to-r from-rose-500 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">start?</span>
          </h2>
        </div>
        <div className="card-grid-auto gap-3">
          <Link
            to="/agreements/finder"
            className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [grid-column:span_2]"
          >
            <Card className="h-full border-violet-300 bg-gradient-to-br from-violet-600 to-indigo-600 text-white transition-transform group-hover:-translate-y-0.5 dark:border-violet-700">
              <CardContent className="flex h-full flex-col justify-between gap-4 p-5">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                  <Compass className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-xl font-bold">What agreement do I need?</h3>
                  <p className="mt-1 text-sm text-violet-100">
                    Tell us the one thing in front of you right now — hiring a co-packer, appointing
                    a distributor, taking money — and get the short list that actually applies.
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-medium">
                  Find my document{" "}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </CardContent>
            </Card>
          </Link>

          {CATEGORIES.map((cat) => {
            const accent = ACCENT_CLASSES[cat.accent];
            const count = countByCategory[cat.id] || 0;
            return (
              <Link
                key={cat.id}
                to={`/agreements/library#cat-${cat.id}`}
                className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Card
                  className={cn(
                    "h-full overflow-hidden border transition-transform group-hover:-translate-y-0.5",
                    accent?.bg,
                    accent?.ring,
                    "ring-1",
                  )}
                >
                  <span className={cn("block h-1 w-full", accent?.dot)} aria-hidden="true" />
                  <CardContent className="flex h-full flex-col gap-3 p-4">
                    <CategoryIcon categoryId={cat.id} />
                    <div>
                      <h3 className="font-semibold leading-tight text-neutral-900">{cat.short}</h3>
                      <p className="mt-1 line-clamp-2 text-xs text-neutral-600">{cat.blurb}</p>
                    </div>
                    <span
                      className={cn(
                        "mt-auto inline-flex w-fit items-center gap-1.5 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ring-1",
                        accent?.text,
                        accent?.ring,
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", accent?.dot)} />
                      {count} {count === 1 ? "document" : "documents"}
                    </span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}

          <TileLink
            to="/agreements/bundles"
            icon={Layers}
            title="Bundles"
            blurb="Pre-built sets for one moment — starting the company, going into retail, raising a round."
            meta={`${BUNDLES.length} bundles`}
          />
          {/* Roadmap + Checklist wrapper — grouped so a single walkthrough
              step can spotlight both cards together. The wrapper spans two
              auto-fill grid columns and nests its own 2-col grid so the
              layout is visually identical to the previous row-of-tiles. */}
          <div
            data-testid="agreements-roadmap-checklist"
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:[grid-column:span_2]"
          >
            <TileLink
              to="/agreements/roadmap"
              icon={Map}
              title="Legal Roadmap"
              blurb="The whole journey from idea to exit, with the agreements you meet at each step."
              meta={`${ROADMAP.length} steps`}
            />
            <TileLink
              to="/agreements/checklist"
              icon={ClipboardCheck}
              title="Master Checklist"
              blurb="Track what is signed, what is outstanding and what renews — across every phase."
              meta={`${CHECKLIST_PHASES.length} phases`}
            />
          </div>
        </div>
      </section>

      {/* ── Start here ───────────────────────────────────────────────────── */}
      {featured.length > 0 && (
        <section>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <div>
              <p className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
                Priority reading
              </p>
              <h2 className="text-2xl font-bold tracking-tight text-neutral-900">
                Start <span className="bg-gradient-to-r from-rose-500 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">here</span>
              </h2>
              <p className="mt-1 text-sm text-neutral-500">
                The documents that decide the most money for the most CPG brands.
              </p>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/agreements/library">
                See all {DOCUMENTS.length} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <div className="card-grid-auto gap-3">
            {featured.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}
    </AgreementsShell>
  );
}

/**
 * The one card on the page that changes with the founder's state:
 * nothing answered → invite the intake; a plan exists → show what it produced.
 */
function PlanCard({ completeness, plan, progress }) {
  const counts = plan?.counts || {};
  const required = counts.required || 0;
  const recommended = counts.recommended || 0;
  const situational = counts.situational || 0;
  const total = counts.total || 0;

  if (!completeness) {
    return (
      <Card className="mb-8 border-indigo-200 bg-indigo-50/70 dark:border-indigo-900 dark:bg-indigo-950/30">
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Get the list that applies to your brand</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Answer a handful of questions about your product, your channels and who you already
              work with. We turn that into a document plan — what you need now, what to line up
              next, and what you can safely ignore.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0 bg-indigo-600 hover:bg-indigo-700">
            <Link to="/agreements/intake">
              Start the brand intake <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (total > 0) {
    return (
      <Card className="mb-8 border-indigo-200 dark:border-indigo-900">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">Your document plan</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Built from your intake, {completeness}% answered.{" "}
                {total} {total === 1 ? "document" : "documents"} matched your brand
                {progress?.total ? ` · ${progress.done} of ${progress.total} marked signed` : ""}.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                <Link to="/agreements/plan" data-testid="agreements-plan-open">
                  Open my plan <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/agreements/intake" data-testid="agreements-plan-edit">Edit answers</Link>
              </Button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
            <Stat label="Required" value={required} tone="text-red-600" />
            <Stat label="Recommended" value={recommended} tone="text-pink-600" />
            <Stat label="Situational" value={situational} tone="text-slate-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8 border-dashed">
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Your intake is {completeness}% answered</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            A few more answers and we can tell you which of the {DOCUMENTS.length} documents in this
            library actually apply to your brand.
          </p>
        </div>
        <Button asChild size="lg" className="shrink-0 bg-indigo-600 hover:bg-indigo-700">
          <Link to="/agreements/intake">
            Continue the intake <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-3 text-center min-w-0">
      <div className={cn("text-2xl font-bold tabular-nums leading-none", tone)}>{value}</div>
      <div className="mt-1 text-[11px] font-medium uppercase leading-tight tracking-wide text-muted-foreground whitespace-nowrap">
        {label}
      </div>
    </div>
  );
}

function TileLink({ to, icon: Icon, title, blurb, meta }) {
  return (
    <Link
      to={to}
      className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card className="h-full border-dashed transition-transform group-hover:-translate-y-0.5">
        <CardContent className="flex h-full flex-col gap-3 p-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800 text-white">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-semibold leading-tight">{title}</h3>
            <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{blurb}</p>
          </div>
          <span className="mt-auto text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {meta}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}
